import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

const LS_KEY = "glp1_intake_step5";

// ✅ FULL QUESTION KEY (important for API payload later)
const QUESTION_KEY =
  "Q5_Have_you_taken_medication_for_weight_loss_within_the_past_4_weeks";

// ✅ FULL ANSWERS ONLY
const ANSWERS = {
  GLP1: "Yes, I've taken GLP-1 medication",
  OTHER: "Yes, I've taken a different medication for weight loss",
  NO: "No",
} as const;

type Answer = (typeof ANSWERS)[keyof typeof ANSWERS] | "";

type OptionCardProps = {
  label: string;
  checked: boolean;
  onChange: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({ label, checked, onChange }) => {
  return (
    <button
      type="button"
      className={`funnel-option funnel-checkbox ${checked ? "is-selected" : ""}`}
      onClick={onChange}
    >
      <span className={`funnel-control radio ${checked ? "is-selected" : ""}`}>
        <span className="dot" />
      </span>

      <span className="funnel-option__label">{label}</span>

      <input className="sr-only" type="radio" checked={checked} onChange={onChange} />
    </button>
  );
};

// ✅ type guard: ensure value is one of the allowed Answer strings
const isAnswer = (v: unknown): v is Exclude<Answer, ""> => {
  return typeof v === "string" && (Object.values(ANSWERS) as readonly string[]).includes(v);
};

// ✅ read FULL answer from LS safely
const readFromLS = (): Answer => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return "";

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const value = parsed?.[QUESTION_KEY];

    return isAnswer(value) ? value : "";
  } catch {
    return "";
  }
};

const NewFunnelStep5: React.FC = () => {
  const navigate = useNavigate();

  // ✅ state holds ONLY the FULL answer string
  const [answer, setAnswer] = useState<Answer>(() => readFromLS());
  const [touched, setTouched] = useState(false);

  // ✅ prevent first-render overwrite
  const didMount = useRef(false);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    if (!answer) return;

    // ✅ store question → answer format
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        [QUESTION_KEY]: answer,
      })
    );
  }, [answer]);

  const onNext = () => {
    setTouched(true);
    if (!answer) return;

    // ✅ routing logic stays same
    if (answer === ANSWERS.NO) {
      navigate("/glp1/intake-form/step-5");
    } else {
      navigate("/glp1/intake-form/step-4a");
    }
  };

  return (
    <FunnelLayout>
      <div className="funnel-section">
        <div className="funnel-q-title funnel-q-step">
          Have you taken medication for weight loss within the past 4 weeks?
        </div>

        <OptionCard
          label={ANSWERS.GLP1}
          checked={answer === ANSWERS.GLP1}
          onChange={() => setAnswer(ANSWERS.GLP1)}
        />

        <OptionCard
          label={ANSWERS.OTHER}
          checked={answer === ANSWERS.OTHER}
          onChange={() => setAnswer(ANSWERS.OTHER)}
        />

        <OptionCard
          label={ANSWERS.NO}
          checked={answer === ANSWERS.NO}
          onChange={() => setAnswer(ANSWERS.NO)}
        />

        {touched && !answer && <div className="funnel-error">Please select one option.</div>}
      </div>

      <button className="next-btn" onClick={onNext} disabled={!answer}>
        Next →
      </button>
    </FunnelLayout>
  );
};

export default NewFunnelStep5;